import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { CreateDesignDemoLicenseParameters } from '../../../../../typings/api/skymap/rest/v0/company';
import { RequestParamsToApiPaths } from '../../../utils/typescript-utils';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { useReloadPage } from '../../hooks/use-reload-page';
import { Button } from '../button/button';
import { Dialog } from '../dialog/dialog';
import { InfoBox } from '../info-box/info-box';
import { List } from '../list/list';
import { Stack } from '../stack/stack';
import { useAddLicenseQuery } from './use-add-license-query';

type Props = {
  onClose: () => void;
};

const CreateDesignLicenseDemoPeriodDialog = (props: Props) => {
  const { t } = useTranslation();
  const { isReloading, reload } = useReloadPage();
  const { handleError, clearErrors, buildErrorList } =
    useErrorHandling<RequestParamsToApiPaths<CreateDesignDemoLicenseParameters>>();
  const addLicenseQuery = useAddLicenseQuery({
    onError: (error) => {
      handleError(error);
    },
    onMutate: () => {
      clearErrors();
    },
  });

  return (
    <Dialog width={600} onClose={props.onClose}>
      {{
        header: t('createDesignLicenseDemoPeriodDialog.title', { ns: 'components' }),
        content: (
          <Content>
            <Stack spacing={1}>
              <span>
                <Trans
                  components={{ br: <br />, bold: <strong /> }}
                  i18nKey="createDesignLicenseDemoPeriodDialog.text"
                  ns="components"
                />
              </span>

              {addLicenseQuery.isSuccess ? (
                <InfoBox color="green">
                  {t('createDesignLicenseDemoPeriodDialog.success', { ns: 'components' })}
                </InfoBox>
              ) : (
                <InfoBox color="yellow">
                  <span>
                    {t('createDesignLicenseDemoPeriodDialog.list.openingSentence', {
                      ns: 'components',
                    })}
                  </span>

                  <List
                    items={[
                      t('createDesignLicenseDemoPeriodDialog.list.item1', { ns: 'components' }),
                      t('createDesignLicenseDemoPeriodDialog.list.importAndEdit', {
                        ns: 'components',
                      }),
                      t('createDesignLicenseDemoPeriodDialog.list.item2', { ns: 'components' }),
                      t('createDesignLicenseDemoPeriodDialog.list.item3', { ns: 'components' }),
                      t('createDesignLicenseDemoPeriodDialog.list.item4', { ns: 'components' }),
                    ]}
                    marker="disc"
                  />
                </InfoBox>
              )}

              {buildErrorList({
                infoBoxProps: {
                  topMargin: false,
                },
              })}
            </Stack>
          </Content>
        ),
        footer: {
          right: (
            <>
              {addLicenseQuery.isSuccess ? (
                <Button
                  color="primary"
                  disabled={isReloading}
                  loading={isReloading}
                  variant="contained"
                  onClick={reload}
                >
                  {t('designLicenseAddedInfoDialog.button', { ns: 'components' })}
                </Button>
              ) : (
                <>
                  <Button
                    color="primary"
                    disabled={addLicenseQuery.isPending}
                    form="designLicenseForm"
                    loading={addLicenseQuery.isPending}
                    variant="contained"
                    onClick={() => addLicenseQuery.mutate()}
                  >
                    {t('createDesignLicenseDemoPeriodDialog.createButton', { ns: 'components' })}
                  </Button>

                  <Button
                    disabled={addLicenseQuery.isPending}
                    variant="text"
                    onClick={props.onClose}
                  >
                    {t('cancel', { ns: 'common' })}
                  </Button>
                </>
              )}
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const Content = styled.div`
  strong {
    font-weight: 500;
  }
`;

export { CreateDesignLicenseDemoPeriodDialog };
